import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, WrenchIcon } from 'lucide-react';
import { RxMark } from './PrescriptionBadge';
import { CtaButton } from './CtaButton';
import { RX_INSERT_UPDATES_URL, RX_INSERT_FALLBACK_MAILTO, rxInsertImages } from '../data/rxInsert';
import { lenses } from '../data/product';
import { trackCustom } from '../utils/tracking';

/**
 * Prescription ready is a buying reason today. The RX Insert is shown as a
 * separate, future accessory so nobody thinks they should wait for it.
 */
export function PrescriptionSection() {
  const updatesHref = RX_INSERT_UPDATES_URL.trim() || RX_INSERT_FALLBACK_MAILTO;
  const isExternal = /^https?:\/\//.test(updatesHref);
  const photos = rxInsertImages.filter(image => image.src.trim());
  return <section id="prescription" aria-labelledby="prescription-heading" className="scroll-mt-28 border-b border-white/10 bg-ink-950" data-id="element-992" data-mp="as77xsdbb0ew">
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-993" data-mp="fsmo0rozam0k">
        <div className="flex items-center gap-3" data-id="element-994" data-mp="2f8u8u091gv6q">
          <RxMark className="h-10 w-10 shrink-0" data-id="element-995" data-mp="2f9m419yq4qwh" />
          <p className="font-display text-xl font-extrabold uppercase tracking-wide text-[#7fe3ec]" data-id="element-996" data-mp="2b78zupzml11t">Prescription ready</p>
        </div>
        <h2 id="prescription-heading" className="grit-headline mt-4 text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase text-offwhite" data-id="element-997" data-mp="2p3vw0qjr7n6o">
          Your vision. Your prescription.
          <br data-id="element-998" data-mp="2y4ank7nb9yim" />
          <span className="text-grit-500" data-id="element-999" data-mp="2u5bh2ilxltp2">Your X1.</span>
        </h2>
        <p className="mt-4 max-w-2xl text-lg text-steel-300" data-id="element-1000" data-mp="2u2z2mffdu1gr">
          Built with prescription users in mind. Prescription users can buy the X1 today.
        </p>

        <p className="mt-10 text-[11px] font-bold uppercase tracking-[0.3em] text-steel-400" data-id="element-1001" data-mp="32yojqc9ss9oc">Two ways to go prescription</p>
        <div className="mt-4 grid gap-px bg-white/10 lg:grid-cols-[1.15fr_0.85fr]" data-id="element-1002" data-mp="3huw2d85k531i">
          {/* Primary: available now */}
          <div className="flex flex-col bg-[#07242a] p-6 sm:p-8" data-id="element-1003" data-mp="3jyrfbk1sw07l">
            <p className="inline-flex self-start bg-[#7fe3ec] px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-ink-950" data-id="element-1004" data-mp="3k0kmd0gbsmg4">
              Available now
            </p>
            <h3 className="mt-4 font-display text-3xl font-extrabold uppercase leading-none text-offwhite sm:text-4xl" data-id="element-1005" data-mp="3tw5kuom6r4l4">
              Prescription ready
            </h3>
            <p className="mt-3 max-w-xl text-[16px] leading-relaxed text-steel-200" data-id="element-1006" data-mp="7i49c7edp1vj">
              Wear prescription lenses? Take your X1 to your trusted optical professional and ask about a custom
              prescription setup.
            </p>
            <ul className="mt-5 grid gap-2" data-id="element-1007" data-mp="giu4r9zq8q8q">
              {['Buy your X1 today', 'Your optical professional handles the prescription', 'Or add the RX Capability Insert to your build'].map(line => <li key={line} className="flex items-start gap-2.5 text-[15px] font-medium text-offwhite" data-id="element-1008" data-mp="2n8h1q0s4him6">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#7fe3ec]" aria-hidden="true" data-id="element-1009" data-mp="2fopnwnmsvze4" />
                    {line}
                  </li>)}
            </ul>
            <div className="mt-auto pt-7" data-id="element-1010" data-mp="25ywmgfgdh0nl">
              <CtaButton href="#build-your-x1" className="w-full sm:w-auto" data-id="element-1011" data-mp="25zo7wxg99i05">
                Build your X1
              </CtaButton>
              <Link to="/legal/terms" className="mt-3 block text-[12px] text-steel-400 underline underline-offset-2 hover:text-offwhite" data-id="element-1012" data-mp="20y8nn7lwjfit">
                Terms apply.
              </Link>
            </div>
          </div>

          {/* Secondary: in development */}
          <div className="flex flex-col bg-ink-900 p-6 sm:p-8" data-id="element-1013" data-mp="2oo2xrheu6pzd">
            <p className="inline-flex items-center gap-1.5 self-start border border-grit-500/60 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-grit-500" data-id="element-1014" data-mp="2jr2s37lwfp8s">
              <WrenchIcon className="h-3 w-3" aria-hidden="true" data-id="element-1015" data-mp="18be4qf4qgo7g" />
              Add-on · +$14.95 each
            </p>
            <h3 className="mt-4 font-display text-2xl font-extrabold uppercase leading-tight text-offwhite sm:text-3xl" data-id="element-1016" data-mp="2x51wak9sxv5f">
              GritVue RX Capability Insert™
            </h3>
            <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1017" data-mp="2n7fcpkhk5ff4">
              Removable · Optical-shop ready · Built for the X1
            </p>
            <p className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-1018" data-mp="2s4eacd0fk4i2">
              The removable insert fits behind the main X1 lens. Take it to your local optical professional, have
              prescription lenses fitted into it, then reinstall it in your X1. Your prescription stays in place while
              you switch outer lenses.
            </p>
            <p className="mt-4 flex flex-wrap items-center gap-2 text-[13px] font-bold uppercase tracking-wide text-offwhite" data-id="element-1019" data-mp="3avovsendzp2k">
              {lenses.map((lens, i) => <React.Fragment key={lens.name} data-id="element-1020" data-mp="1zicpzy82gwdr" data-mpi={i}>
                  {i > 0 && <span className="text-steel-500" data-id="element-1021" data-mp="1zfqitzp38iw2">·</span>}
                  <span className="flex items-center gap-1.5" data-id="element-1022" data-mp="hj5bquh4c1tp">
                    <span className="h-3 w-3 rounded-full border border-white/25" style={{
                  backgroundColor: lens.swatch
                }} aria-hidden="true" data-id="element-1023" data-mp="cldi9w0ts7ne" />
                    {lens.name}
                  </span>
                </React.Fragment>)}
            </p>

            <div className="mt-6 border-t border-white/10 pt-5" data-id="element-1024" data-mp="3uzmelhoqh5yn">
              <p className="text-[14px] leading-relaxed text-steel-300" data-id="element-1025" data-mp="3q0jiimqqea2t">
                Available on every X1: Essential, Complete System and multipacks. On multipacks, choose only as many
                inserts as your crew needs.
              </p>
              <p className="mt-3 text-[13px] text-steel-400" data-id="element-1026" data-mp="2t7ezq54i3sw">
                Insert only. Prescription lenses are not included and are fitted by your optical professional.
              </p>
            </div>

            <a href="#build-your-x1" className="mt-6 inline-flex items-center justify-center gap-2 self-start border-2 border-grit-500 px-5 py-3 font-display text-lg font-extrabold uppercase tracking-wide text-grit-500 transition-colors duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1027" data-mp="377pf8q2h06ef">
              Add RX to your build
              <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1028" data-mp="ratj4sgnvfoq" />
            </a>
            <a href={updatesHref} {...isExternal ? {
            target: '_blank',
            rel: 'noopener noreferrer'
          } : {}} onClick={() => trackCustom('rx_insert_updates_clicked')} className="mt-3 self-start text-[12px] text-steel-400 underline underline-offset-2 hover:text-offwhite" data-id="element-1029" data-mp="w8l9hi1nqtqt">
              Questions about RX? Contact support
            </a>
          </div>
        </div>

        {photos.length > 0 && <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-id="element-1030" data-mp="2hoai42096kyz">
            {photos.map(photo => <li key={photo.caption} data-id="element-1031" data-mp="109fq974uo3k">
                <figure className="border border-white/10 bg-ink-900" data-id="element-1032" data-mp="10jasb71blg4">
                  <img src={photo.src} alt={photo.alt} className="aspect-square w-full object-contain" loading="lazy" data-id="element-1033" data-mp="3sdx6f5ebwh4l" />
                  <figcaption className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-1034" data-mp="3nesysj71yrgd">
                    {photo.caption} · Prototype
                  </figcaption>
                </figure>
              </li>)}
          </ul>}
      </div>
    </section>;
}